import React from 'react'
import { InformationCircleIcon, ArrowRightIcon } from '@heroicons/react/24/outline'

interface RedditAppSetupGuideProps {
  redirectUri: string
}

const RedditAppSetupGuide: React.FC<RedditAppSetupGuideProps> = ({ redirectUri }) => {
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
      <div className="flex items-start">
        <InformationCircleIcon className="h-6 w-6 text-blue-500 flex-shrink-0 mt-0.5" />
        <div className="ml-4 flex-1">
          <h4 className="text-lg font-medium text-blue-900 mb-3">
            How to Create Your Reddit App
          </h4>
          <div className="space-y-3 text-sm text-blue-800">
            <div className="flex items-start">
              <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-blue-200 text-blue-900 font-semibold text-xs mr-3 flex-shrink-0">
                1
              </span>
              <div>
                <p className="font-medium">Go to Reddit Apps Page</p>
                <a
                  href="https://www.reddit.com/prefs/apps"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700 underline inline-flex items-center mt-1"
                >
                  https://www.reddit.com/prefs/apps
                  <ArrowRightIcon className="h-3 w-3 ml-1" />
                </a>
              </div>
            </div>

            <div className="flex items-start">
              <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-blue-200 text-blue-900 font-semibold text-xs mr-3 flex-shrink-0">
                2
              </span>
              <div>
                <p className="font-medium">Create New App</p>
                <p className="text-blue-700 mt-1">
                  Scroll to the bottom and click <strong>"create app"</strong> or{' '}
                  <strong>"create another app"</strong>
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-blue-200 text-blue-900 font-semibold text-xs mr-3 flex-shrink-0">
                3
              </span>
              <div>
                <p className="font-medium">Fill in App Details</p>
                <ul className="list-disc list-inside text-blue-700 mt-1 space-y-1 ml-4">
                  <li>
                    <strong>Name:</strong> Any name you want (e.g., "ReddMiner App")
                  </li>
                  <li>
                    <strong>App type:</strong> Select <strong>"web app"</strong>
                  </li>
                  <li>
                    <strong>Description:</strong> Optional
                  </li>
                  <li>
                    <strong>About URL:</strong> Optional
                  </li>
                  <li>
                    <strong>Redirect URI:</strong>{' '}
                    <code className="bg-blue-100 px-2 py-0.5 rounded text-xs">
                      {redirectUri}
                    </code>
                    <button
                      onClick={() => navigator.clipboard.writeText(redirectUri)}
                      className="ml-2 text-blue-600 hover:text-blue-700 text-xs underline"
                    >
                      Copy
                    </button>
                  </li>
                </ul>
              </div>
            </div>

            <div className="flex items-start">
              <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-blue-200 text-blue-900 font-semibold text-xs mr-3 flex-shrink-0">
                4
              </span>
              <div>
                <p className="font-medium">Copy Your Credentials</p>
                <p className="text-blue-700 mt-1">
                  After creating the app:
                </p>
                <ul className="list-disc list-inside text-blue-700 mt-1 space-y-1 ml-4">
                  <li>
                    <strong>Client ID:</strong> The string under your app name (looks like{' '}
                    <code className="bg-blue-100 px-1 py-0.5 rounded text-xs">abc123XYZ</code>)
                  </li>
                  <li>
                    <strong>Secret:</strong> Next to "secret:" (click to reveal if needed)
                  </li>
                </ul>
              </div>
            </div>

            <div className="flex items-start">
              <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-blue-200 text-blue-900 font-semibold text-xs mr-3 flex-shrink-0">
                5
              </span>
              <div>
                <p className="font-medium">Paste Credentials Below</p>
                <p className="text-blue-700 mt-1">
                  Enter your Client ID and Secret in the form below, then click "Save Credentials"
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RedditAppSetupGuide
